/**
 * SiteForm 모델 - 서버 통신 + UI 편집용
 * 서버와의 주고받음과 UI 폼 편집을 모두 처리
 */

import { ISite } from './Site';

/**
 * 위치 정보 인터페이스
 */
export interface ILocation {
  coordinates: [number, number]; // [경도, 위도]
  crs: {
    properties: { name: string };
    type: string;
  };
  type: string;
}

/**
 * 회사 정보 인터페이스
 */
export interface ICompany {
  id: number | null;
  companyName: string;
}

/**
 * 프로젝트 정보 인터페이스
 */
export interface IProject {
  name: string;
  id: number;
}

/**
 * 담당자 정보 인터페이스
 */
export interface IManager {
  id: number;
  name: string;
  position: string;
  companySeq: number;
}

/**
 * 사이트 폼 데이터 인터페이스 (서버 통신 + UI 편집용)
 */
export interface ISiteForm {
  id?: number;                    // 생성 시에는 없음
  name: string;
  address: string;
  establishedDate: string | null;
  pvModuleMaker: string;
  powerCapacity: number;
  pvModuleCount: number;
  pvModuleOutput: number;
  description: string;
  rotation: number;
  location: ILocation;
  ownerCompany: ICompany;
  operationCompany: ICompany;
  managers: IManager[];
  status: string;
  type: string;
  project: IProject;
  panelProductSeq: number | null;
  
  // UI 전용 필드들
  siteImageFile?: File | null;    // 파일 업로드용
  isDirty?: boolean;              // 변경 여부
  errors?: string[];              // 유효성 검사 오류
  selectedManagers?: IManager[];  // 선택된 담당자들
}

/**
 * 기본값 객체들
 */
export const SiteFormDefaults = {
  location: {
    coordinates: [127.37957, 36.372795] as [number, number],
    crs: { properties: { name: 'EPSG:4326' }, type: 'name' },
    type: 'Point'
  } as ILocation,
  
  company: {
    id: null,
    companyName: ''
  } as ICompany,
  
  project: {
    name: '',
    id: 0
  } as IProject,
  
  manager: {
    id: 0,
    name: '',
    position: '',
    companySeq: 0
  } as IManager,
  
  form: {
    id: undefined,
    name: '',
    address: '',
    establishedDate: null,
    pvModuleMaker: '',
    powerCapacity: 0,
    pvModuleCount: 0,
    pvModuleOutput: 400,
    description: '',
    rotation: 0,
    location: { coordinates: [127.37957, 36.372795], crs: { properties: { name: 'EPSG:4326' }, type: 'name' }, type: 'Point' },
    ownerCompany: { id: null, companyName: '' },
    operationCompany: { id: null, companyName: '' },
    managers: [],
    status: 'active',
    type: 'solar',
    project: { name: '', id: 0 },
    panelProductSeq: null,
    siteImageFile: null,
    isDirty: false,
    errors: [],
    selectedManagers: []
  } as ISiteForm
};

/**
 * 팩토리 함수들
 */
export const SiteFormFactory = {
  /**
   * 빈 폼 데이터 생성
   */
  createEmpty(): ISiteForm {
    return { ...SiteFormDefaults.form };
  },
  
  /**
   * API 응답에서 폼 데이터 생성 (서버 → 클라이언트)
   */
  fromApiResponse(apiResponse: any): ISiteForm {
    if (!apiResponse) return this.createEmpty();
    
    return {
      id: apiResponse.id || apiResponse.seq || undefined,
      name: apiResponse.name || '',
      address: apiResponse.address || '',
      establishedDate: apiResponse.establishedDate || null,
      pvModuleMaker: apiResponse.pvModuleMaker || '',
      powerCapacity: apiResponse.powerCapacity || 0,
      pvModuleCount: apiResponse.pvModuleCount || 0,
      pvModuleOutput: apiResponse.pvModuleOutput || 0,
      description: apiResponse.description || '',
      rotation: apiResponse.rotation || 0,
      location: apiResponse.location || SiteFormDefaults.location,
      ownerCompany: apiResponse.ownerCompany || SiteFormDefaults.company,
      operationCompany: apiResponse.operationCompany || SiteFormDefaults.company,
      managers: apiResponse.managers || [],
      status: apiResponse.status || 'active',
      type: apiResponse.type || 'solar',
      project: apiResponse.project || SiteFormDefaults.project,
      panelProductSeq: apiResponse.panelProductSeq || null,
      siteImageFile: null,
      isDirty: false,
      errors: [],
      selectedManagers: apiResponse.managers || []
    };
  },
  
  /**
   * 폼 데이터를 API 요청 형태로 변환 (클라이언트 → 서버)
   */
  toApiRequest(form: ISiteForm): any {
    if (!form) return null;
    
    // UI 전용 필드 제외하고 서버로 전송할 데이터만 추출
    const { 
      siteImageFile, 
      isDirty, 
      errors, 
      selectedManagers,
      ...apiData 
    } = form;
    
    return apiData;
  },
  
  /**
   * Site 객체에서 폼 데이터 생성
   */
  fromSite(site: ISite): ISiteForm {
    if (!site) return this.createEmpty();
    
    return {
      ...site,
      siteImageFile: null,
      isDirty: false,
      errors: [],
      selectedManagers: site.managers || []
    };
  },
  
  /**
   * 폼 데이터를 Site 객체로 변환
   */
  toSite(form: ISiteForm): ISite | null {
    if (!form) return null;
    
    // UI 전용 필드 제외
    const { 
      siteImageFile, 
      isDirty, 
      errors, 
      selectedManagers,
      ...siteData 
    } = form;
    
    return siteData as ISite;
  }
};

/**
 * 기본 export
 */
export default {
  SiteFormDefaults,
  SiteFormFactory
};
